import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { assetCatalog, type Manufacturer } from "@/api/client";
import { useConfirm } from "@/components/ConfirmDialog";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { ago, dateTime } from "@/lib/format";
import { ManufacturerForm, QK, Thumb, kindLabel, useManufacturers, useTemplates } from "@/modules/asset_catalog/common";
import { CAP, useSession } from "@/auth/session";

const MFR_CHIP: StatusMap = {
  active: { label: "ACTIVE", color: "var(--ok)" },
  archived: { label: "ARCHIVED", color: "var(--t-mute)" },
};

/** Manufacturers: who makes what — first-class so every model carries a
 *  real supplier record (warranty, support contact) instead of a free-text name. */
export function ManufacturersPage() {
  const qc = useQueryClient();
  const fb = useMutationFeedback();
  const { ask, dialog } = useConfirm();
  const q = useManufacturers();
  const tpl = useTemplates().data;
  const rows = q.data?.manufacturers ?? [];
  const [mode, setMode] = useState<"idle" | "new" | "edit">("idle");
  const [edit, setEdit] = useState<Manufacturer | null>(null);
  const [done, setDone] = useState("");

  const { can: canDo } = useSession();
  // MANAGE_CATALOG: the catalogue is platform-level (plans.py).
  const archive = useMutation({
    mutationFn: (id: string) => assetCatalog.archiveManufacturer(id),
    onSuccess: (m) => { fb.ok(); setDone(`${m.name} archived`); void qc.invalidateQueries({ queryKey: [...QK, "manufacturers"] }); },
    onError: fb.fail,
  });

  const cols = useMemo<DataCol<Manufacturer>[]>(() => [
    { key: "logo", label: "", width: 44, render: (m) => <Thumb src={m.logo ? assetCatalog.logoUrl(m.manufacturer_id, m.logo.captured_at) : null} /> },
    { key: "abbr", label: "Code", width: 70, mono: true, render: (m) => m.abbr ?? "—" },
    { key: "name", label: "Name", width: 200, render: (m) => <span style={{ fontWeight: 600 }}>{m.name}</span> },
    { key: "country", label: "Country", width: 80, mono: true, render: (m) => m.country ?? "—" },
    { key: "supplies", label: "Supplies", width: 240, render: (m) => m.supplies.length ? m.supplies.map((k) => kindLabel(tpl, k)).join(", ") : "—",
      title: (m) => m.supplies.map((k) => kindLabel(tpl, k)).join(", ") },
    { key: "models", label: "Models", width: 80, mono: true, render: (m) => m.models },
    { key: "status", label: "Status", width: 100, render: (m) => <StatusChip st={m.status} map={MFR_CHIP} /> },
    { key: "support", label: "Support contact", width: 220, render: (m) => m.support_contact ?? "—" },
    { key: "created", label: "Added", width: 90, mono: true, render: (m) => ago(m.created_at), title: (m) => dateTime(m.created_at) },
    { key: "act", label: "", width: 150, render: (m) => !canDo(CAP.MANAGE_CATALOG) ? null : (
      <span style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
        <button className="btn ghost sm" onClick={() => { setEdit(m); setMode("edit"); setDone(""); }}>Edit</button>
        {m.status === "active" && (
          <button className="btn ghost sm" disabled={m.models > 0}
            title={m.models > 0 ? "Retire its models first" : "Archive (hidden from pickers; nothing deleted)"}
            onClick={() => ask(`Archive ${m.name}? It disappears from pickers; nothing is deleted.`, () => archive.mutate(m.manufacturer_id), "Archive")}>
            Archive
          </button>
        )}
      </span>
    ) },
  ], [archive, ask, tpl, canDo]);

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Catalog · Manufacturers" title="Manufacturers"
        desc="Suppliers of cells, parts and complete assets. A manufacturer with models cannot be archived — retire the models first."
        right={!canDo(CAP.MANAGE_CATALOG) ? null : mode === "idle"
          ? <button className="btn teal sm" onClick={() => { setEdit(null); setMode("new"); setDone(""); }}>Add manufacturer</button>
          : <button className="btn ghost sm" onClick={() => setMode("idle")}>← Back</button>} />

      {mode !== "idle" && (
        <div className="panel panel-pad" style={{ marginBottom: 16 }}>
          <div className="section-label">{mode === "new" ? "New manufacturer" : `Edit ${edit?.name}`}</div>
          <ManufacturerForm initial={mode === "edit" ? edit : null} onCancel={() => setMode("idle")}
            onDone={(m) => { setDone(`${m.name} saved`); setMode("idle"); }} />
        </div>
      )}
      {(done || fb.err) && <div style={{ marginBottom: 10, fontSize: 12.5, color: fb.err ? "var(--bad)" : "var(--ok)" }}>{fb.err || done}</div>}

      <DataTable rows={rows} cols={cols} rowKey={(r) => r.manufacturer_id} pageSize={100}
        empty={<div className="panel panel-pad cap">
          No manufacturers yet — add the first one; every model needs one.
        </div>} />
      {dialog}
    </div>
  );
}
